import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  Button,
  Stack,
  Flex,
  SimpleGrid,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiPackage, FiSettings, FiRefreshCw, FiUpload } from 'react-icons/fi';

const Feature = ({ title, text, icon }: { title: string; text: string; icon: React.ReactElement }) => {
  return (
    <Stack align={'center'} textAlign={'center'}>
      <Flex
        w={16}
        h={16}
        align={'center'}
        justify={'center'}
        color={'white'}
        rounded={'full'}
        bg={'brand.500'}
        mb={1}
      >
        {icon}
      </Flex>
      <Text fontWeight={600} fontSize="lg">{title}</Text>
      <Text color={'gray.600'} _dark={{ color: 'gray.400' }}>
        {text}
      </Text>
    </Stack>
  );
};

export default function HomePage() {
  return (
    <Box>
      {/* Hero Section */}
      <Box
        bg={useColorModeValue('gray.50', 'gray.900')}
        color={useColorModeValue('gray.700', 'gray.200')}
        py={20}
        px={8}
        mb={10}
        borderRadius="lg"
      >
        <Stack
          spacing={4}
          as={Box}
          textAlign={'center'}
          maxW={'3xl'}
          mx={'auto'}
        >
          <Heading
            fontWeight={600}
            fontSize={{ base: '2xl', sm: '4xl', md: '6xl' }}
            lineHeight={'110%'}
            color="brand.500"
          >
            Model Context Protocol <br />
            <Text as={'span'} color={'gray.900'} _dark={{ color: 'gray.100' }}>
              Package Manager
            </Text>
          </Heading>
          <Text color={'gray.500'} fontSize={'xl'}>
            A pip-style package manager for MCP servers. Easily install, configure,
            and manage Model Context Protocol servers.
          </Text>
          <Stack
            direction={'column'}
            spacing={3}
            align={'center'}
            alignSelf={'center'}
            position={'relative'}
            mt={5}
          >
            <Button
              as={RouterLink}
              to="/packages"
              colorScheme={'brand'}
              bg={'brand.500'}
              rounded={'full'}
              px={6}
              size="lg"
              _hover={{
                bg: 'brand.600',
              }}
            >
              Browse Packages
            </Button>
            <Button
              as={RouterLink}
              to="/docs/cli"
              variant={'link'}
              colorScheme={'blue'}
              size={'sm'}
            >
              Learn More
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* Features Section */}
      <Box py={12}>
        <Stack spacing={4} as={Box} textAlign={'center'} mb={10}>
          <Heading fontSize={'3xl'}>Key Features</Heading>
          <Text color={'gray.600'} _dark={{ color: 'gray.400' }}>
            MCPpm provides powerful tools for managing MCP servers
          </Text>
        </Stack>

        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={10} px={5}>
          <Feature
            icon={<Icon as={FiPackage} w={10} h={10} />}
            title={'Install & Remove'}
            text={'Easily install MCP servers from GitHub repositories and remove them when no longer needed.'}
          />
          <Feature
            icon={<Icon as={FiSettings} w={10} h={10} />}
            title={'Configure'}
            text={'Configure MCP servers with environment variables and settings through a simple CLI.'}
          />
          <Feature
            icon={<Icon as={FiRefreshCw} w={10} h={10} />}
            title={'Start & Stop'}
            text={'Control MCP server processes with simple commands to start, stop, and restart.'}
          />
          <Feature
            icon={<Icon as={FiUpload} w={10} h={10} />}
            title={'Publish'}
            text={'Publish your own MCP servers to the registry for others to discover and use.'}
          />
        </SimpleGrid>
      </Box>

      {/* Call to Action */}
      <Box
        bg={useColorModeValue('brand.50', 'gray.800')}
        py={12}
        px={8}
        mt={10}
        borderRadius="lg"
      >
        <Stack
          direction={{ base: 'column', md: 'row' }}
          spacing={4}
          justify="space-between"
          align="center"
        >
          <Stack spacing={4} textAlign={{ base: 'center', md: 'left' }} maxW="lg">
            <Heading fontSize={'2xl'}>Ready to get started?</Heading>
            <Text color={'gray.600'} _dark={{ color: 'gray.400' }}>
              Install MCPpm and start managing your MCP servers today.
            </Text>
          </Stack>
          <Button
            as={RouterLink}
            to="/docs/installation"
            colorScheme={'brand'}
            bg={'brand.500'}
            rounded={'full'}
            px={6}
            _hover={{
              bg: 'brand.600',
            }}
          >
            Installation Guide
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
